/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { FC, useEffect, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { hasInternalView } from 'utils/showDetails';

import { Envelope, EnvelopeStatus, EnvelopeWithStatus } from 'types/Envelope';

import DataTablePagination from 'modules/_shared/components/Tables/DataTablePagination';

import 'modules/envelopes/styles/EnvelopesListRecentsSectionTable.scss';
import { smallPaginationItems } from 'modules/_shared/components/Tables/paginationItems';
import EnvelopesListRecentsSectionRow from './EnvelopesListRecentsSectionRow';

const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 10;

type EnvelopesRecentsSectionTableProps = {
  envelopes: Envelope[];
  envelopesStatus: EnvelopeStatus[];
  envelopesTotal: number;
  showAllColumns?: boolean;
  serverPage?: number;
  serverRowsPerPage?: number;
  onServerPageChange?: (page: number) => void; // eslint-disable-line no-unused-vars
  onServerRowsPerPageChange?: (limit: number) => void; // eslint-disable-line no-unused-vars
  onRefresh?: () => Promise<void>;
};

const EnvelopesListRecentsSectionTable: FC<
  EnvelopesRecentsSectionTableProps
> = ({
  envelopes,
  envelopesStatus,
  envelopesTotal,
  showAllColumns = false,
  serverPage,
  serverRowsPerPage,
  onServerPageChange,
  onServerRowsPerPageChange,
  onRefresh,
}) => {
  const { t } = useTranslate();

  const [currentPage, setCurrentPage] = useState<number>(
    serverPage || INITIAL_PAGE
  );
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    serverRowsPerPage || INITIAL_ROWS_PER_PAGE
  );

  const [pagination, setPagination] = useState({
    customItemPerPage: INITIAL_ROWS_PER_PAGE,
    itemPerPage: INITIAL_ROWS_PER_PAGE,
    currentPage: INITIAL_PAGE,
    totalItems: 0,
    totalPages: 0,
  });

  const getVisibleEnvelopeStatuses = (): EnvelopeStatus[] => {
    if (!envelopesStatus?.length) return [];

    if (
      typeof serverPage === 'number' ||
      typeof serverRowsPerPage === 'number'
    ) {
      return envelopesStatus;
    }
    if (rowsPerPage === 0) return envelopesStatus;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return envelopesStatus.slice(startIndex, endIndex);
  };

  const getIndexBasedOnCurrentPage = (index: number) =>
    index + (currentPage - 1) * rowsPerPage;

  const getCurrentEnvelope = (envelopeId: string) =>
    envelopes.find((envelope) => envelope.envelopeId === envelopeId) || {};

  const handleChangePage = (page: number) => {
    if (onServerPageChange) onServerPageChange(page);
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (rowsCount: number) => {
    if (onServerRowsPerPageChange) onServerRowsPerPageChange(rowsCount);
    setRowsPerPage(rowsCount);
    setCurrentPage(INITIAL_PAGE);
  };

  useEffect(() => {
    setPagination({
      customItemPerPage: rowsPerPage,
      itemPerPage: rowsPerPage,
      currentPage,
      totalItems: envelopesTotal,
      totalPages: Math.ceil(envelopesTotal / rowsPerPage),
    });
  }, [currentPage, rowsPerPage, envelopesTotal]);

  useEffect(() => {
    if (typeof serverPage === 'number') setCurrentPage(serverPage);
  }, [serverPage]);

  useEffect(() => {
    if (typeof serverRowsPerPage === 'number')
      setRowsPerPage(serverRowsPerPage);
  }, [serverRowsPerPage]);

  return (
    <div className="table-envelopes-wrapper">
      <table className="table">
        <thead>
          <tr className="nk-tb-item nk-tb-head">
            <th className="nk-tb-col d-none d-md-table-cell" />

            {hasInternalView() && (
              <th className="nk-tb-col">
                <h6 className="overline-title mb-05">
                  {t('EnvelopesColumn_Id')}
                </h6>
              </th>
            )}

            <th className="nk-tb-col">
              <h6 className="overline-title mb-05">
                {t('EnvelopesColumn_Subject')}
              </h6>
            </th>
            <th className="nk-tb-col d-none d-md-table-cell">
              <h6 className="overline-title mb-05">
                {t('EnvelopesColumn_SentDate')}
                <em className="icon ni ni-arrow-down ml-1" />
              </h6>
            </th>

            {showAllColumns && (
              <>
                <th className="nk-tb-col">
                  <h6 className="overline-title mb-05">
                    {t('EnvelopesColumn_ExpirationDate')}
                  </h6>
                </th>
                <th className="nk-tb-col">
                  <h6 className="overline-title mb-05">
                    {t('EnvelopesColumn_LastModified')}
                  </h6>
                </th>
              </>
            )}

            <th className="nk-tb-col">
              <h6 className="overline-title mb-05">
                {t('EnvelopesColumn_Status')}
              </h6>
            </th>

            {showAllColumns && (
              <>
                <th className="nk-tb-col">
                  <h6 className="overline-title mb-05">
                    {t('EnvelopesColumn_HaveAllSigners')}
                  </h6>
                </th>
                <th className="nk-tb-col">
                  <h6 className="overline-title mb-05">
                    {t('EnvelopesColumn_PendingSigners')}
                  </h6>
                </th>
                <th className="nk-tb-col">
                  <h6 className="overline-title mb-05">
                    {t('EnvelopesColumn_CompletedSigners')}
                  </h6>
                </th>
                <th className="nk-tb-col">
                  <h6 className="overline-title mb-05">
                    {t('EnvelopesColumn_DeclinedSigners')}
                  </h6>
                </th>
              </>
            )}

            {hasInternalView() && (
              <th className="nk-tb-col">
                <h6 className="overline-title mb-05">
                  {t('EnvelopesColumn_Resend')}
                </h6>
              </th>
            )}

            <th className="nk-tb-col d-none d-md-table-cell">
              <h6 className="overline-title mb-05">
                {t('EnvelopesColumn_HaveSigned')}
              </h6>
            </th>
            <th className="nk-tb-col d-none d-lg-table-cell" />
          </tr>
        </thead>

        <tbody>
          {getVisibleEnvelopeStatuses().map(
            (envelope: EnvelopeStatus, index: number) => (
              <EnvelopesListRecentsSectionRow
                key={envelope.envelopeInfo.envelopeId}
                envelope={
                  {
                    ...getCurrentEnvelope(envelope.envelopeInfo.envelopeId),
                    envelopeStatus: envelope,
                  } as EnvelopeWithStatus
                }
                envelopes={envelopes}
                index={getIndexBasedOnCurrentPage(index)}
                showAllColumns={showAllColumns}
                onRefresh={onRefresh}
              />
            )
          )}
        </tbody>
      </table>

      <DataTablePagination
        itemPerPage={pagination.itemPerPage}
        totalItems={pagination.totalItems}
        currentPage={pagination.currentPage}
        customItemPerPage={pagination.customItemPerPage}
        paginationItems={smallPaginationItems}
        paginate={handleChangePage}
        setRowsPerPage={setRowsPerPage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        className="card-pagination-small border-top bg-lighter"
      />
    </div>
  );
};

export default EnvelopesListRecentsSectionTable;
